import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, formatApiError } from '../api';

const AuthContext = createContext(null);

const ACTIONS = ['view', 'create', 'edit', 'approve', 'deactivate', 'export'];

// Permission rows come from /auth/me and /auth/login as flat rows with a
// `module_slug` column (SELECT p.*, m.slug AS module_slug ...).  The role
// editor's own endpoint nests the module instead; accept both so the menu and
// the buttons never depend on which endpoint filled the user.
const slugOf = (p) => p?.module_slug || p?.module?.slug || null;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      const response = await authApi.getMe();
      if (response.success) {
        setUser(response.data);
      } else {
        setUser(null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
      }
    } catch (err) {
      setUser(null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    setError(null);
    try {
      const response = await authApi.login(email, password);
      if (response.success) {
        const { user: userData, access_token } = response.data;
        localStorage.setItem('access_token', access_token);
        // The sidebar filters by permissions on its first render after login.
        // The login response now carries them; if an older API did not, load
        // the profile before rendering so a role user is not shown an empty menu.
        let fullUser = userData;
        if (!Array.isArray(userData?.permissions)) {
          try {
            const me = await authApi.getMe();
            if (me?.success && me.data) fullUser = me.data;
          } catch (err) {
            // fall through with the login payload; the menu fails open
          }
        }
        setUser(fullUser);
        localStorage.setItem('user', JSON.stringify(fullUser));
        return { success: true };
      }
      return { success: false, error: response.message };
    } catch (err) {
      const errorMsg = formatApiError(err);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (err) {
      // Ignore logout errors
    } finally {
      setUser(null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    }
  };

  const isSuperAdmin = user?.role?.name === 'Super Admin';
  const permissionsLoaded = Array.isArray(user?.permissions);

  /** Does the role hold `action` on `module`?  Strict: used for buttons. */
  const hasPermission = (module, action) => {
    if (!user) return false;
    if (isSuperAdmin) return true;
    const permissions = Array.isArray(user.permissions) ? user.permissions : [];
    return permissions.some((p) => slugOf(p) === module && !!p[`can_${action}`]);
  };

  /** Does the role hold any action on any of these modules?  Used to show a menu entry. */
  const hasModuleAccess = (modules) => {
    if (!user) return false;
    if (isSuperAdmin) return true;
    const list = Array.isArray(modules) ? modules : [modules];
    const permissions = Array.isArray(user.permissions) ? user.permissions : [];
    return permissions.some((p) => list.includes(slugOf(p)) && ACTIONS.some((a) => !!p[`can_${a}`]));
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    hasPermission,
    hasModuleAccess,
    permissionsLoaded,
    isAuthenticated: !!user,
    isSuperAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
